import React, { useState, useEffect, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { colors } from "../AgenticPaymentAirdrop/styles";
import { currencyCountry, withCountry } from "../../utils/currencyCountry";
import {
  card, h2, note, help, label, input, th, thNum, td, tdNum, mono, tableBox, grid, btn, ghostBtn, dangerBtn, linkBtn,
  Tag, Banner, num, amount, when, sentence, percent, signed, signedTone, signedPercent, Field, ReasonField, isLegacyPool, sortRows, useSort,
  hashscanTx, shortTx,
} from "./shared";

// Drain protection for the local-token pools (remediation guide 4A.3), set
// through lp-api, which passes it on to the signing server. Every local pool
// is listed with its backing: the USDb it holds against what its holders are
// owed at today's rate, a figure the server works out. Pools on the profit
// ledger are held at exactly 100%, and what they make trading since then is
// their profit. Every change goes out with a reason; the server adds the
// admin's email. Against a signing server without the ledger (no tradingPaused
// in the settings) the ledger parts stay hidden. The one-time setup tools
// (putting pools at 100%, splitting the opening, revaluing) are on Pool Setup,
// and minting pool profit is on Profit Mint.


// The settings hold fractions; the form shows percentages.
export const toPercent = (f) => (f == null ? "" : String(Number((num(f) * 100).toFixed(4))));
export const toFraction = (p) => Number((Number(p) / 100).toFixed(6));

// 99.5% to 100.5%, as the cell shows it (one decimal), is fine. Below that is
// a warning, and under 50% the pool pauses. A pool on the ledger should read
// 100%, so outside the band there is a problem.
export const backingTone = (b, onLedger) => {
  if (b == null) return null;
  const tenths = Math.round(num(b) * 1000);
  if (tenths >= 995 && tenths <= 1005) return "good";
  if (onLedger) return "bad";
  return b < 0.5 ? "bad" : b < 1 ? "warn" : "good";
};

// Red at the loss limit, where the pool pauses; amber from half of it.
export const lossTone = (loss, limit) => {
  if (!(num(loss) > 0) || !(num(limit) > 0)) return null;
  return num(loss) >= num(limit) ? "bad" : num(loss) >= num(limit) / 2 ? "warn" : null;
};

// A profit % on a pool that holds almost nothing, or one over 1000%, says
// nothing useful, so it shows as "-".
export const profitPercentShown = (p) =>
  p.profitPercent != null && num(p.reserve) >= 1 && Math.abs(num(p.profitPercent)) <= 10;

// "Spread +1.00, fees +0.50, rate moves -2.00": where a profit came from. Profit
// counts trading only; a signing server older than adjustmentUsdb still folds
// the opening and corrections in, so those show too.
export const profitBreakdown = (p) => {
  const kinds = p.profitByKind || {};
  const parts = [`Spread ${signed(kinds.spread)}`, `fees ${signed(p.feesUsdb)}`, `rate moves ${signed(kinds.fx)}`];
  if (!("adjustmentUsdb" in p)) parts.push(`opening ${signed(kinds.opening)}`, `corrections ${signed(kinds.correction)}`);
  return parts.join(", ");
};

// The signing server pauses a pool under 50% backed on its next quote, so until
// then it isn't really trading.
export const pausesNext = (p) => !p.pausedScope && p.backing != null && num(p.backing) < 0.5;

export const matchesSearch = (p, q) => {
  const term = q.trim().toLowerCase();
  return !term || [p.currencySymbol, currencyCountry(p.currencySymbol), p.tokenId].some((v) => String(v || "").toLowerCase().includes(term));
};

// A pool with next to no USDb and nothing owed to customers. The overview lists
// every pool that isn't deleted, most of them never used.
export const notUsed = (p) => !p.pausedScope && num(p.reserve) < 0.005 && num(p.targetReserve) < 0.01;

// "Active tokens only" is remembered per viewer. Storage can be missing or
// blocked, and then the box just starts unchecked.
const ACTIVE_ONLY_KEY = "bc_pools_active_only";
const readActiveOnly = () => {
  try {
    return window.localStorage.getItem(ACTIVE_ONLY_KEY) === "1";
  } catch (e) {
    return false;
  }
};
const saveActiveOnly = (on) => {
  try {
    window.localStorage.setItem(ACTIVE_ONLY_KEY, on ? "1" : "0");
  } catch (e) {
    // Not remembered; the box still works for this visit.
  }
};

export const statusLabel = (p) => (p.pausedScope ? "Paused"
  : pausesNext(p) ? "Will pause on next swap"
  : p.status && p.status !== "ACTIVE" ? sentence(String(p.status).toLowerCase())
  : notUsed(p) ? "Not used yet"
  : "Trading");

// What the "?" after each Pools header says.
export const POOL_HELP = {
  reserve: "USDb this pool holds for its currency.",
  owed: "What customers' tokens are worth at today's mid rate. Internal wallets are left out.",
  profit: "Spread, fees and rate moves since the pool went to 100%. The opening adjustment and later corrections made by a person don't count. Hover over a number to see the split.",
  profitPercent: "Profit ÷ in pool. Shows - when the pool holds under 1 USDb.",
  fees: "Swap fees this pool collected.",
  paidOut: "USDb paid out of this pool in the last 24 hours, against its daily cap.",
  status: "Trading, or Paused with the reason under it. A pool under 50% backed pauses on its next swap.",
};
// The signing server sends how often it revalues the pools; older ones don't.
export const backingHelp = (minutes) => `In pool ÷ owed to customers. Green from 99.5% to 100.5%.${
  num(minutes) > 0 ? ` Revalued every ${num(minutes) === 1 ? "minute" : `${num(minutes)} minutes`}.` : ""}`;
export const lossHelp = (limit) => `What rate moves cost this pool in the last 24 hours. ${
  num(limit) > 0 ? `Over ${amount(limit)} USDb` : "Over the loss limit"}, the pool pauses.`;

// What each column header sorts by (see sortRows).
const SORTS = {
  currency: { text: true, value: (p) => p.currencySymbol },
  reserve: { value: (p) => p.reserve },
  owed: { value: (p) => p.targetReserve },
  backing: { value: (p) => (notUsed(p) ? null : p.backing) },
  profit: { value: (p) => p.profitUsdb },
  profitPercent: { value: (p) => (profitPercentShown(p) ? p.profitPercent : null) },
  fees: { value: (p) => p.feesUsdb },
  loss: { value: (p) => p.loss24h },
  paidOut: { value: (p) => p.outflow24h },
  status: { text: true, value: statusLabel },
};
const PERCENT_FIELDS = [
  { key: "maxDailyOutflowFraction", label: "Daily payout per pool", help: (min) => `Share of what customers are owed that a pool can pay out in 24 hours. Never less than ${amount(min)} USDb.` },
];

const settingsForm = (s = {}) => ({
  outflowCapEnabled: !!s.outflowCapEnabled,
  lossBreakerUsdb: s.lossBreakerUsdb == null ? "" : String(s.lossBreakerUsdb),
  ...Object.fromEntries(PERCENT_FIELDS.map((f) => [f.key, toPercent(s[f.key])])),
});

// Checks the limits form. Returns an error message, or null when it can be sent.
export const settingsError = (form, hasLedger = false) => {
  if (hasLedger && !(Number(form.lossBreakerUsdb) > 0)) return "Loss limit per pool: enter an amount above 0.";
  for (const f of PERCENT_FIELDS) {
    const v = Number(form[f.key]);
    if (form[f.key] === "" || !(v > 0 && v <= 100)) return `${f.label}: enter a percentage above 0 and up to 100.`;
  }
  return null;
};

const ACTIONS = {
  params: "k changed",
  settings: "Limits changed",
  pause: "Paused",
  resume: "Resumed",
  outflow_raise: "Payout cap raised",
  target_reset: "Target reset",
  ledger_open: "Balanced to 100%",
};
const LEDGER_KINDS = {
  spread: "Spread",
  fee: "Fee",
  fx: "Rate move",
  supply: "Outside swaps",
  opening: "Opening",
  correction: "Correction",
  internal: "Internal wallet",
};
const FIELD_NAMES = {
  tradingPaused: "All trading paused",
  tradingPausedReason: "Why",
  tradingPausedAt: "Since",
  lossBreakerUsdb: "Loss limit",
  outflowCapEnabled: "Payout cap",
  usdbAllocation: "In pool",
  customerCirculating: "Customer tokens",
  ledgerMid: "Rate",
  limitsEnabled: "Per-customer limits",
  maxTradeFraction: "Largest single trade",
  maxDailyOutflowFraction: "Daily payout",
  maxImpact: "Largest price impact",
  accountDailyLimitUnverified: "Unverified cap",
  accountDailyLimitVerified: "Verified cap",
  targetReserve: "Target",
  slippageK: "k",
  outflowCapRaise: "Raised cap",
  outflowCapRaiseUntil: "Until",
  pausedScope: "Paused",
  pausedReason: "Why",
  confirmedMid: "Confirmed rate",
};
const FRACTIONS = new Set(["maxTradeFraction", "maxDailyOutflowFraction", "maxImpact"]);
const DATES = new Set(["outflowCapRaiseUntil", "tradingPausedAt"]);
const AMOUNTS = new Set(["usdbAllocation", "customerCirculating", "lossBreakerUsdb", "ledgerMid", "confirmedMid"]);
const show = (k, v) => {
  if (v == null) return "none";
  if (typeof v === "boolean") return v ? "on" : "off";
  if (k === "pausedScope") return "all swaps";
  if (FRACTIONS.has(k)) return `${toPercent(v)}%`;
  if (DATES.has(k)) return when(v);
  if (AMOUNTS.has(k)) return amount(v);
  return String(v);
};
// A trading pause or resume is saved as a settings change, so its title comes
// from tradingPaused.
const tradingToggled = (c) => c.action === "settings" && c.after?.tradingPaused != null
  && !!c.after.tradingPaused !== !!c.before?.tradingPaused;
export const changeTitle = (c) => (tradingToggled(c)
  ? (c.after.tradingPaused ? "Trading paused" : "Trading resumed")
  : ACTIONS[c.action] || c.action);

// "k none → 0.4, Raised cap none → 500": only the fields that changed. A field
// that only went empty is left out, and so is the pause flag the title already
// names.
export const describeChange = (c) => {
  const before = c.before || {};
  const after = c.after || {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return keys
    .filter((k) => after[k] != null && show(k, before[k]) !== show(k, after[k]))
    .filter((k) => !(k === "tradingPaused" && tradingToggled(c)))
    .map((k) => `${FIELD_NAMES[k] || k} ${show(k, before[k])} → ${show(k, after[k])}`)
    .join(", ");
};

// "revalued from mid 18181.4855" reads "Revalued from 18,181.49".
export const ledgerNote = (n) => {
  const m = /^revalued from mid (-?[\d.]+)$/i.exec(String(n || "").trim());
  return m ? `Revalued from ${amount(m[1])}` : n;
};

// A row that comes to 0.00 USDb and 0 tokens, as the table rounds them, says nothing.
export const emptyLedgerRow = (r) => Math.round(num(r.usdb) * 100) === 0 && Math.round(num(r.tokens) * 100) === 0;

// The rows of one pool's ledger, newest first, in a panel over the page.
const LedgerDrawer = ({ pool, rows, error, hederaNetwork, onClose }) => (
  <div role="dialog" aria-label={`${withCountry(pool.currencySymbol)} ledger`}
    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}
    onClick={onClose}>
    <div style={{ background: colors.white, width: "min(760px, 100%)", height: "100%", overflowY: "auto", padding: "20px 24px", boxSizing: "border-box" }}
      onClick={(e) => e.stopPropagation()}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <h2 style={{ ...h2, margin: 0 }}>{withCountry(pool.currencySymbol)} ledger</h2>
        <button type="button" style={ghostBtn(false)} onClick={onClose}>Close</button>
      </div>
      <p style={note}>
        {pool.ledgerSince && `At 100% since ${when(pool.ledgerSince)}. `}What this pool made or lost, newest first. Plus is a gain. Shows the latest 100 rows.
      </p>
      {error ? <Banner tone="bad">{error}</Banner>
        : rows == null ? <p style={note}>Loading…</p>
        : rows.filter((r) => !emptyLedgerRow(r)).length === 0 ? <p style={note}>Nothing on the ledger yet.</p> : (
        <div style={tableBox("calc(100vh - 140px)")}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>When</th><th style={th}>Kind</th><th style={thNum}>USDb</th><th style={thNum}>Tokens</th><th style={thNum}>Rate</th><th style={th}>Note</th></tr></thead>
            <tbody>
              {rows.filter((r) => !emptyLedgerRow(r)).map((r, i) => (
                <tr key={r._id || `${r.at}-${i}`}>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{when(r.at)}</td>
                  <td style={td}>{LEDGER_KINDS[r.kind] || r.kind}</td>
                  <td style={{ ...tdNum, ...signedTone(r.usdb) }}>{signed(r.usdb)}</td>
                  <td style={tdNum}>{r.tokens == null ? "-" : amount(r.tokens)}</td>
                  <td style={tdNum}>{r.mid == null ? "-" : num(r.mid).toLocaleString("en-US", { maximumFractionDigits: 6 })}</td>
                  <td style={td}>
                    {ledgerNote(r.note) || "-"}
                    {r.actor && <div style={help}>{r.actor}</div>}
                    {r.swapTransactionId && (
                      <div style={mono}>
                        <a href={hashscanTx(hederaNetwork, r.swapTransactionId)} target="_blank" rel="noopener noreferrer" title={r.swapTransactionId}>
                          {shortTx(r.swapTransactionId)}
                        </a>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

const PoolProtection = ({
  overviewUrl, settingsUrl, ledgerUrl, resumeUrl, raiseUrl, setupUrl, profitMintPageUrl, hederaNetwork,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null); // { tone, text }
  const [settings, setSettings] = useState(settingsForm());
  const [settingsReason, setSettingsReason] = useState("");
  const [action, setAction] = useState(null); // { kind: "resume" | "raise", pool, value, reason }
  const [tradingReason, setTradingReason] = useState(null); // null while the pause/resume form is closed
  const [drawer, setDrawer] = useState(null); // { pool, rows, error }
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(readActiveOnly);
  const { sort, sortTh } = useSort(SORTS, { key: "currency", dir: "asc" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest(overviewUrl);
      if (res?.success) {
        setData(res.data);
        setSettings(settingsForm(res.data?.settings));
        setLoadError(null);
      } else {
        setLoadError(res?.error || "Couldn't load pool protection.");
      }
    } catch (e) {
      setLoadError("Couldn't load pool protection.");
    } finally {
      setLoading(false);
    }
  }, [overviewUrl]);

  useEffect(() => { load(); }, [load]);

  const send = async (url, body, done) => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await postRequest(url, body);
      if (res?.success) {
        setMessage({ tone: "good", text: done });
        await load();
        return true;
      }
      setMessage({ tone: "bad", text: res?.error || "That didn't save." });
    } catch (e) {
      setMessage({ tone: "bad", text: e?.data?.error || "That didn't save." });
    } finally {
      setBusy(false);
    }
    return false;
  };

  const hasLedger = !!data?.settings && "tradingPaused" in data.settings;
  const tradingPaused = !!data?.settings?.tradingPaused;

  const saveSettings = async () => {
    const error = settingsError(settings, hasLedger) || (settingsReason.trim() ? null : "Enter a reason.");
    if (error) return setMessage({ tone: "bad", text: error });
    const body = {
      ...Object.fromEntries(PERCENT_FIELDS.map((f) => [f.key, toFraction(settings[f.key])])),
      ...(hasLedger ? { outflowCapEnabled: settings.outflowCapEnabled, lossBreakerUsdb: Number(settings.lossBreakerUsdb) } : {}),
      reason: settingsReason.trim(),
    };
    if (await send(settingsUrl, body, "Limits saved.")) setSettingsReason("");
  };

  const setTrading = async () => {
    const reason = (tradingReason || "").trim();
    if (!reason) return setMessage({ tone: "bad", text: "Enter a reason." });
    const pause = !tradingPaused;
    const done = pause ? "All trading is paused." : "Trading is back on.";
    if (await send(settingsUrl, { tradingPaused: pause, reason }, done)) setTradingReason(null);
  };

  const showLedger = async (pool) => {
    setDrawer({ pool, rows: null, error: null });
    try {
      const res = await getRequest(`${ledgerUrl}?${new URLSearchParams({ tokenId: pool.tokenId, limit: "100" })}`);
      const rows = Array.isArray(res?.data) ? res.data : res?.data?.entries || res?.data?.rows;
      setDrawer({ pool, rows: res?.success ? rows || [] : null, error: res?.success ? null : res?.error || "Couldn't load the ledger." });
    } catch (e) {
      setDrawer({ pool, rows: null, error: "Couldn't load the ledger." });
    }
  };

  const open = (kind, pool) => {
    setAction({ kind, pool, value: "", reason: "" });
    setMessage(null);
  };

  const runAction = async () => {
    const { kind, pool, value } = action;
    const reason = action.reason.trim();
    const symbol = withCountry(pool.currencySymbol);
    let error = null;
    if (kind === "raise" && !(Number(value) > 0)) error = "New cap: enter an amount above 0.";
    error = error || (reason ? null : "Enter a reason.");
    if (error) return setMessage({ tone: "bad", text: error });
    let ok;
    if (kind === "resume") {
      ok = await send(resumeUrl, { tokenId: pool.tokenId, reason }, `${symbol} is trading again.`);
    } else {
      ok = await send(raiseUrl, { tokenId: pool.tokenId, cap: Number(value), reason }, `${symbol} can pay out up to ${amount(value)} USDb for the next 24 hours.`);
    }
    if (ok) setAction(null);
  };

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
      <div>
        <p style={{ ...note, margin: 0 }}>Monitor local currency pools and pause trading when needed. All changes are recorded in the audit log.</p>
      </div>
      <div style={{ display: "flex", gap: "8px" }}>
        {setupUrl && <a href={setupUrl} style={{ ...ghostBtn(false), textDecoration: "none" }}>Pool setup</a>}
        {profitMintPageUrl && <a href={profitMintPageUrl} style={{ ...ghostBtn(false), textDecoration: "none" }}>Profit mint</a>}
        <button type="button" style={ghostBtn(loading)} disabled={loading} onClick={load}>{loading ? "Refreshing…" : "Refresh"}</button>
      </div>
    </div>
  );

  if (!data) {
    return (
      <div>
        {header}
        {loading ? <p style={note}>Loading…</p> : <Banner tone="bad">{loadError} Refresh to try again.</Banner>}
      </div>
    );
  }

  const pools = (data.pools || []).filter((p) => !isLegacyPool(p));
  const inUse = pools.filter((p) => !notUsed(p)).length;
  const shown = sortRows(pools.filter((p) => matchesSearch(p, search) && !(activeOnly && notUsed(p))), sort, SORTS);
  const changes = data.recentChanges || [];
  const symbolOf = (id) => withCountry(pools.find((p) => p.tokenId === id)?.currencySymbol) || id;
  const minCap = data.settings?.outflowCapMinUsdb ?? 25;
  // Older signing servers always cap payouts; on the ledger the cap is optional.
  const capsOn = !hasLedger || !!data.settings?.outflowCapEnabled;
  const lossLimit = data.settings?.lossBreakerUsdb;
  const actionPanel = action && (
    <div style={{ marginTop: "16px", padding: "16px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.gray100 }}>
      <p style={{ margin: "0 0 12px", fontSize: "14px" }}>
        {action.kind === "resume" && `Resume ${withCountry(action.pool.currencySymbol)}? Swaps start again, and today's rate becomes the one the pool checks against.`}
        {action.kind === "raise" && `Raise how much ${withCountry(action.pool.currencySymbol)} can pay out in 24 hours. The new cap lasts 24 hours, then the normal cap applies again.`}
      </p>
      <div style={{ ...grid, marginBottom: "12px" }}>
        {action.kind === "raise" && (
          <Field id="action-cap" title="New cap" suffix="USDb" type="number" min="0" step="any"
            hint={`Paid out in the last 24 hours: ${amount(action.pool.outflow24h)} USDb. Cap now: ${action.pool.outflowCap == null ? "unknown" : `${amount(action.pool.outflowCap)} USDb`}.`}
            value={action.value} onChange={(e) => setAction({ ...action, value: e.target.value })} />
        )}
      </div>
      <div style={{ marginBottom: "12px" }}>
        <ReasonField id="action-reason" value={action.reason} onChange={(reason) => setAction({ ...action, reason })} />
      </div>
      <button type="button" style={btn(busy)} disabled={busy} onClick={runAction}>
        {action.kind === "resume" ? "Resume pool" : "Raise cap for 24 hours"}
      </button>
      <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy} onClick={() => setAction(null)}>Cancel</button>
    </div>
  );

  return (
    <div>
      {header}
      {loadError && <Banner tone="bad">{loadError} Refresh to try again.</Banner>}
      {tradingPaused && (
        <Banner tone="bad">
          <strong>All trading is paused.</strong> No pool takes swaps until someone resumes it.
          {data.settings.tradingPausedReason && <> Why: {data.settings.tradingPausedReason}.</>}
          {data.settings.tradingPausedAt && <> Since {when(data.settings.tradingPausedAt)}.</>}
        </Banner>
      )}
      {message && <Banner tone={message.tone}>{message.text}</Banner>}

      {hasLedger && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
            <div>
              <h2 style={h2}>Trading {tradingPaused ? <Tag tone="bad">Paused</Tag> : <Tag tone="good">On</Tag>}</h2>
              <p style={{ ...note, margin: 0 }}>Pause all swaps on every pool. Trading also pauses automatically if a pool's balance doesn't match its ledger.</p>
            </div>
            {tradingReason == null && (
              <button type="button" style={tradingPaused ? btn(busy) : dangerBtn(busy)} disabled={busy}
                onClick={() => { setTradingReason(""); setMessage(null); }}>
                {tradingPaused ? "Resume all trading" : "Pause all trading"}
              </button>
            )}
          </div>
          {tradingReason != null && (
            <div style={{ marginTop: "16px", padding: "16px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.gray100 }}>
              <p style={{ margin: "0 0 12px", fontSize: "14px" }}>
                {tradingPaused ? "Resume all trading? Every pool that isn't paused on its own takes swaps again." : "Pause all trading? No pool takes swaps until someone resumes it."}
              </p>
              <div style={{ marginBottom: "12px" }}>
                <ReasonField id="trading-reason" value={tradingReason} onChange={setTradingReason} />
              </div>
              <button type="button" style={tradingPaused ? btn(busy) : dangerBtn(busy)} disabled={busy} onClick={setTrading}>
                {tradingPaused ? "Resume all trading" : "Pause all trading"}
              </button>
              <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy} onClick={() => setTradingReason(null)}>Cancel</button>
            </div>
          )}
        </div>
      )}

      <div style={card}>
        <h2 style={h2}>Pools</h2>
        <p style={note}>
          Every local currency pool. Owed to customers is what customers' tokens are worth at today's rate.
          {hasLedger && ` A pool at 100% that loses more than ${amount(lossLimit)} USDb to rate moves in 24 hours pauses.`}
        </p>
        {pools.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px 20px", flexWrap: "wrap", marginBottom: "12px" }}>
            <input type="search" aria-label="Search pools" placeholder="Search by currency, country or token ID" style={{ ...input, maxWidth: "320px" }}
              value={search} onChange={(e) => setSearch(e.target.value)} />
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
              <input type="checkbox" checked={activeOnly} onChange={(e) => { setActiveOnly(e.target.checked); saveActiveOnly(e.target.checked); }} />
              Active tokens only
            </label>
            <span style={{ fontSize: "13px", color: colors.gray600 }}>
              {activeOnly ? `Showing ${shown.length} of ${inUse} in use` : `Showing ${shown.length} of ${pools.length} · ${inUse} in use`}
            </span>
          </div>
        )}
        {pools.length === 0 ? <p style={{ ...note, margin: 0 }}>No pools yet.</p>
          : shown.length === 0 ? <p style={{ ...note, margin: 0 }}>No pool matches.</p> : (
          <div style={tableBox()}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: "1%", textAlign: "right" }}>#</th>
                  {sortTh("currency", "Currency")}{sortTh("reserve", "In pool (USDb)", true, POOL_HELP.reserve)}
                  {sortTh("owed", "Owed to customers (USDb)", true, POOL_HELP.owed)}
                  {sortTh("backing", "Backing", true, backingHelp(data.revalueEveryMinutes))}
                  {hasLedger && (
                    <>
                      {sortTh("profit", "Profit (USDb)", true, POOL_HELP.profit)}{sortTh("profitPercent", "Profit %", true, POOL_HELP.profitPercent)}
                      {sortTh("fees", "Fees (USDb)", true, POOL_HELP.fees)}{sortTh("loss", "Loss 24h (USDb)", true, lossHelp(lossLimit))}
                    </>
                  )}
                  {capsOn && sortTh("paidOut", "Paid out 24h / cap", true, POOL_HELP.paidOut)}
                  {sortTh("status", "Status", false, POOL_HELP.status)}<th style={th} />
                </tr>
              </thead>
              <tbody>
                {shown.map((p, i) => {
                  const paused = !!p.pausedScope;
                  const unused = notUsed(p);
                  const tone = unused ? null : backingTone(p.backing, p.onLedger);
                  const breakdown = profitBreakdown(p);
                  const lTone = lossTone(p.loss24h, lossLimit);
                  const inactive = p.status && p.status !== "ACTIVE";
                  return (
                    <tr key={p.tokenId}>
                      <td style={{ ...tdNum, color: colors.gray600 }}>{i + 1}</td>
                      <td style={td}>
                        <strong>{withCountry(p.currencySymbol)}</strong><div style={{ ...mono, color: colors.gray600 }}>{p.tokenId}</div>
                      </td>
                      <td style={tdNum}>{p.reserve == null ? "-" : amount(p.reserve)}</td>
                      <td style={tdNum}>{p.targetReserve == null ? "-" : amount(p.targetReserve)}</td>
                      <td style={tdNum}>
                        {tone ? <Tag tone={tone}>{percent(p.backing)}</Tag> : "-"}
                      </td>
                      {hasLedger && (
                        <>
                          <td style={{ ...tdNum, ...signedTone(p.profitUsdb) }} title={breakdown}>
                            {p.profitUsdb == null ? "-" : signed(p.profitUsdb)}
                          </td>
                          <td style={{ ...tdNum, ...(profitPercentShown(p) ? signedTone(p.profitPercent, 3) : {}) }}>{profitPercentShown(p) ? signedPercent(p.profitPercent) : "-"}</td>
                          <td style={tdNum}>{p.feesUsdb == null ? "-" : amount(p.feesUsdb)}</td>
                          <td style={tdNum}>
                            {p.loss24h == null ? "-" : lTone ? <Tag tone={lTone}>{amount(p.loss24h)}</Tag> : amount(p.loss24h)}
                          </td>
                        </>
                      )}
                      {capsOn && (
                        <td style={tdNum}>
                          {amount(p.outflow24h)} / {p.outflowCap == null ? "-" : amount(p.outflowCap)}
                          {p.outflowCapUsed != null && <div style={help}>{percent(p.outflowCapUsed)} used</div>}
                          {p.outflowCapRaise != null && <div style={help}>Raised until {when(p.outflowCapRaiseUntil)}</div>}
                        </td>
                      )}
                      <td style={td}>
                        <Tag tone={paused ? "bad" : pausesNext(p) ? "warn" : inactive ? "info" : unused ? "neutral" : "good"}>{statusLabel(p)}</Tag>
                        {paused && p.pausedReason && <div style={help}>{sentence(p.pausedReason)}. Since {when(p.pausedAt)}.</div>}
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        {paused && <button type="button" style={{ ...linkBtn, marginLeft: 0 }} onClick={() => open("resume", p)}>Resume</button>}
                        {capsOn && <button type="button" style={paused ? linkBtn : { ...linkBtn, marginLeft: 0 }} onClick={() => open("raise", p)}>Raise cap</button>}
                        {hasLedger && <button type="button" style={paused || capsOn ? linkBtn : { ...linkBtn, marginLeft: 0 }} onClick={() => showLedger(p)}>Ledger</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {actionPanel}
      </div>

      <div style={card}>
        <h2 style={h2}>Limits</h2>
        <p style={note}>Applies to all pools.</p>
        {hasLedger && (
          <>
            <div style={grid}>
              <Field id="lossBreakerUsdb" title="Loss limit per pool" suffix="USDb" type="number" min="0" step="any"
                hint="A pool that loses more than this to rate moves in 24 hours pauses."
                value={settings.lossBreakerUsdb} onChange={(e) => setSettings({ ...settings, lossBreakerUsdb: e.target.value })} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 600, marginBottom: "4px" }}>
              <input type="checkbox" checked={settings.outflowCapEnabled}
                onChange={(e) => setSettings({ ...settings, outflowCapEnabled: e.target.checked })} />
              Turn on the daily payout cap per pool
            </label>
            <p style={note}>Off by default. A pool at 100% only ever pays out what customers are owed.</p>
          </>
        )}
        {(!hasLedger || settings.outflowCapEnabled) && (
          <div style={grid}>
            {PERCENT_FIELDS.map((f) => (
              <Field key={f.key} id={f.key} title={f.label} hint={f.help(minCap)} suffix="%" type="number" min="0" max="100" step="any"
                value={settings[f.key]} onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })} />
            ))}
          </div>
        )}
        <div style={{ marginBottom: "12px" }}>
          <ReasonField id="settings-reason" value={settingsReason} onChange={setSettingsReason} />
        </div>
        <button type="button" style={btn(busy)} disabled={busy} onClick={saveSettings}>Save limits</button>
      </div>

      <div style={card}>
        <h2 style={h2}>Audit log</h2>
        {changes.length === 0 ? <p style={{ ...note, margin: 0 }}>No changes yet.</p> : (
          <div style={tableBox()}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>When</th><th style={th}>Pool</th><th style={th}>Change</th><th style={th}>By</th><th style={th}>Reason</th></tr></thead>
              <tbody>
                {changes.map((c, i) => (
                  <tr key={c._id || `${c.at}-${i}`}>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{when(c.at)}</td>
                    <td style={td}>{c.tokenId ? symbolOf(c.tokenId) : "All pools"}</td>
                    <td style={td}><strong>{changeTitle(c)}</strong>{describeChange(c) && <div style={help}>{describeChange(c)}</div>}</td>
                    <td style={td}>{c.actor}</td>
                    <td style={td}>{c.reason || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {drawer && <LedgerDrawer {...drawer} hederaNetwork={hederaNetwork} onClose={() => setDrawer(null)} />}
    </div>
  );
};

export default PoolProtection;
